/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// ESTIMATE TO TASKS CONVERSION HOOK
// ============================================================================

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import { logger } from "@/lib/utils/logger";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
export interface ConvertEstimateToTasksParams {
  projectId: string;
  estimateId: string;
  projectStartDate: string; // ISO datetime string
  selectedWorkAreaIds?: string[];
  includeUnassigned?: boolean;
}

export interface ConvertEstimateToTasksResult {
  tasksCreated: number;
  changeOrderNumber: number;
  isBaseEstimate: boolean;
  warnings?: string[];
}

/**
 * Hook for converting an approved estimate to project tasks
 */
export function useEstimateToTasksConversion() {
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled: organizationReady } =
    useOrganizationQueryGuard({
      enabled: true,
      debugKey: "useEstimateToTasksConversion",
    });
  const queryClient = useQueryClient();

  const api = createApiClient({
    getToken,
    getOrganizationId: () => organizationId ?? undefined,
    autoRetry429: true,
  });

  const conversionMutation = useMutation({
    mutationFn: async (params: ConvertEstimateToTasksParams) => {
      if (!organizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }
      const body: Record<string, unknown> = {
        project_id: params.projectId,
        project_start_date: params.projectStartDate,
      };
      if (params.selectedWorkAreaIds?.length) {
        body.selected_work_area_ids = params.selectedWorkAreaIds;
      }
      if (params.includeUnassigned) {
        body.include_unassigned = true;
      }
      const response = await api.post<ConvertEstimateToTasksResult>(
        `/api/estimates/${params.estimateId}/convert-to-tasks`,
        body,
      );

      if (isApiFailure(response)) {
        toastFromApiFailure("Failed to convert estimate to tasks", response);
        throw new Error(response.message);
      }

      return response.data as ConvertEstimateToTasksResult;
    },
    onSuccess: (data) => {
      toast.success("Success", {
        description: `Successfully created ${data.tasksCreated} task(s) from estimate`,
      });
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["project-tasks", variables.projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", variables.projectId, "tasks"],
      });
      queryClient.invalidateQueries({
        queryKey: ["estimate", variables.estimateId],
      });
    },
    onError: (error: Error) => {
      // Error is already handled in mutationFn via toastFromApiFailure
      logger.error("Estimate to tasks conversion error", {
        error: error.message,
      });
    },
  });

  return {
    convertToTasks: conversionMutation.mutateAsync,
    isConverting: conversionMutation.isPending,
    error: conversionMutation.error,
  };
}
